import React, {useState, useEffect, FunctionComponent} from "react";
import { useTranslation } from "react-i18next";
import { getAuth } from "firebase/auth";
import { FIREBASE_APP } from "../../services/firebase/config";
import { NeumorphicContainer } from "./NeumorphicContainer";
import { getUser } from "../../services/firebase/firestore/user";
import { UserAccount } from "../../interfaces/UserAccount";

// Import raw GoPerson and IconBaseProps
import { GoPerson as GoPersonRawImport } from "react-icons/go";
import { IconBaseProps } from "react-icons"; // Correct import from root 'react-icons'

// Explicitly assert GoPerson as a FunctionComponent.
// This is the strongest type assertion to bypass the TS error.
const GoPerson: FunctionComponent<IconBaseProps> = GoPersonRawImport as unknown as FunctionComponent<IconBaseProps>;

interface WelcomeUserProps {
  className?: string; // Additional class names for styling
  onClick?: () => void;
}

export const WelcomeUser: React.FC<WelcomeUserProps> = ({ className, onClick }) => {
  const { t } = useTranslation();
  const [userDisplayName, setUserDisplayName] = useState<string>("User");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const auth = getAuth(FIREBASE_APP);
    const unsubscribeAuth = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // Set initial display name and photo from Firebase Auth
        setUserDisplayName(authUser.displayName || authUser.email?.split('@')[0] || "User");
        setProfileImageUrl(authUser.photoURL);

        if (authUser.uid) {
          setIsLoading(true);
          try {
            const userAccount: UserAccount | undefined = await getUser(authUser.uid);
            if (userAccount) {
              // Prioritize Firestore data
              setUserDisplayName(userAccount.displayName || authUser.displayName || authUser.email?.split('@')[0] || "User");
              setProfileImageUrl(userAccount.profileImage || authUser.photoURL || null);
            } else {
              console.warn(`UserAccount not found in Firestore for UID: ${authUser.uid}`);
            }
          } catch (error) {
            console.error("Error fetching user data from Firestore:", error);
            // Fallback to Firebase Auth info in case of Firestore error
            setUserDisplayName(authUser.displayName || authUser.email?.split('@')[0] || "User");
            setProfileImageUrl(authUser.photoURL);
          } finally {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false); // No UID, no Firestore fetch
        }
      } else {
        // No user logged in
        setUserDisplayName("Guest");
        setProfileImageUrl(null);
        setIsLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  if (isLoading) {
    return (
      <NeumorphicContainer className={`welcome-user-container ${className || ""}`}>
        <span className="welcome-user-text">{t("welcomeUser.loading")}</span>
      </NeumorphicContainer>
    );
  }

  // If no user is logged in and not loading, we might return null or a generic message.
  // For now, if no name is available, don't render this component.
  if (!userDisplayName && !isLoading) {
     return null;
  }

  return (
    <NeumorphicContainer className={`welcome-user-container ${className || ""}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      {profileImageUrl ? (
        <img src={profileImageUrl} alt={t("welcomeUser.profileAlt", { name: userDisplayName })} className="welcome-user-avatar" />
      ) : (
        // Use the GoPerson icon when no profile image is available
        <div className="welcome-user-avatar-placeholder">
          <GoPerson size={40} color="#888" className="welcome-user-icon" />
        </div>
      )}
      <span className="welcome-user-text">
        {t("welcomeUser.welcomeMessage", { name: userDisplayName.split(" ")[0] })}
      </span>
    </NeumorphicContainer>
  );
};
